import type { WorkflowsTableFiltersType } from "#src/batteries-included-components/FilterAreas/WorkflowsFilterAreas/WorkflowsFilterAreas.helpers";
import type { WorkflowsViewFiltersType } from "#src/batteries-included-components/FilterAreas/WorkflowsFilterAreas/WorkflowsFilterAreas";
import { getDefaultValuesAndStoredFilters } from "#src/components/FilterArea/FilterArea.helpers";
import { WorkflowTemplateApiVersion } from "@validereinc/domain";
import {
  Assets,
  type AssetTypeSelectionType,
} from "@validereinc/domain-controllers";
import { toFlattenedObject } from "@validereinc/utilities";

const { AssetTypeSelection } = Assets.view;

export const WorkflowListSubjectFilterType = {
  ASSET: "asset",
  DATASET: "dataset",
} as const;

export type WorkflowListSubjectFilterTypeValue =
  (typeof WorkflowListSubjectFilterType)[keyof typeof WorkflowListSubjectFilterType];

export const WorkflowListSubjectFilterDropdownValue = {
  DATASET: "dataset",
} as const;

export type WorkflowListSubjectFilterDropdownValueType =
  | (typeof Assets.view.AssetTypeSelection)[keyof typeof Assets.view.AssetTypeSelection]
  | typeof WorkflowListSubjectFilterDropdownValue.DATASET;

export type WorkflowListSubjectFilterDropdownOptionType = {
  label: string;
  value: WorkflowListSubjectFilterDropdownValueType;
};

/** Orders workflow list subject filter options with Dataset above All Assets. */
export const getWorkflowListSubjectFilterDropdownOptions = (
  assetTypeOptions: WorkflowListSubjectFilterDropdownOptionType[]
): WorkflowListSubjectFilterDropdownOptionType[] => {
  const allAssetsOption = assetTypeOptions.find(
    (option) => option.value === AssetTypeSelection.ALL_ASSETS
  );
  const assetTypeOptionsWithoutAllAssets = assetTypeOptions.filter(
    (option) => option.value !== AssetTypeSelection.ALL_ASSETS
  );

  return [
    ...assetTypeOptionsWithoutAllAssets,
    {
      label: "Dataset",
      value: WorkflowListSubjectFilterDropdownValue.DATASET,
    },
    ...(allAssetsOption ? [allAssetsOption] : []),
  ];
};

/** Returns whether the workflows list is filtered by dataset subject. */
export const isWorkflowListDatasetSubjectFilter = (
  subjectFilterType?: WorkflowListSubjectFilterTypeValue
): boolean => subjectFilterType === WorkflowListSubjectFilterType.DATASET;

type WorkflowListSubjectFilterState = Partial<
  Pick<WorkflowsViewFiltersType, "subjectFilterType" | "subjectFilterSelection">
>;

/** Returns whether dataset subject filtering is active from persisted filter state. */
export const isWorkflowListDatasetSubjectFilterActive = (
  filters: WorkflowListSubjectFilterState
): boolean =>
  filters.subjectFilterSelection ===
    WorkflowListSubjectFilterDropdownValue.DATASET ||
  isWorkflowListDatasetSubjectFilter(filters.subjectFilterType);

/** Maps the combined subject dropdown value to persisted filter fields. */
export const workflowListSubjectDropdownValueToFilters = (
  value: WorkflowListSubjectFilterDropdownValueType
): Pick<WorkflowsViewFiltersType, "subjectFilterType" | "assetType"> => {
  if (value === WorkflowListSubjectFilterDropdownValue.DATASET) {
    return {
      subjectFilterType: WorkflowListSubjectFilterType.DATASET,
    };
  }

  return {
    subjectFilterType: WorkflowListSubjectFilterType.ASSET,
    assetType: value,
  };
};

/** Resolves the combined subject dropdown value from persisted filter fields. */
export const workflowListSubjectFiltersToDropdownValue = ({
  subjectFilterType,
  subjectFilterSelection,
  assetType,
  defaultAssetType,
}: {
  subjectFilterType?: WorkflowListSubjectFilterTypeValue;
  subjectFilterSelection?: WorkflowsViewFiltersType["subjectFilterSelection"];
  assetType?: WorkflowsViewFiltersType["assetType"];
  defaultAssetType: (typeof AssetTypeSelection)[keyof typeof AssetTypeSelection];
}): WorkflowListSubjectFilterDropdownValueType => {
  if (
    isWorkflowListDatasetSubjectFilterActive({
      subjectFilterType,
      subjectFilterSelection,
    })
  ) {
    return WorkflowListSubjectFilterDropdownValue.DATASET;
  }

  const resolvedAssetType = assetType ?? defaultAssetType;

  if (
    subjectFilterSelection &&
    subjectFilterSelection !== WorkflowListSubjectFilterDropdownValue.DATASET &&
    (subjectFilterSelection !== defaultAssetType ||
      resolvedAssetType === defaultAssetType)
  ) {
    return subjectFilterSelection;
  }

  return resolvedAssetType;
};

export const normalizeWorkflowListViewFilters = (
  filters: Partial<WorkflowsViewFiltersType>,
  {
    defaultAssetType,
  }: {
    defaultAssetType: (typeof AssetTypeSelection)[keyof typeof AssetTypeSelection];
  }
): Pick<
  WorkflowsViewFiltersType,
  "subjectFilterType" | "subjectFilterSelection" | "assetType"
> => {
  const subjectFilterSelection = workflowListSubjectFiltersToDropdownValue({
    subjectFilterType: filters.subjectFilterType,
    subjectFilterSelection: filters.subjectFilterSelection,
    assetType: filters.assetType,
    defaultAssetType,
  });

  const isDatasetSubject =
    subjectFilterSelection === WorkflowListSubjectFilterDropdownValue.DATASET;

  return {
    subjectFilterType: isDatasetSubject
      ? WorkflowListSubjectFilterType.DATASET
      : WorkflowListSubjectFilterType.ASSET,
    subjectFilterSelection,
    assetType: isDatasetSubject
      ? (filters.assetType ?? defaultAssetType)
      : subjectFilterSelection,
  };
};

export const getWorkflowsViewFilterDefaultValues = async (
  storedFilters: Partial<WorkflowsViewFiltersType>,
  {
    defaultAssetType,
  }: {
    defaultAssetType: AssetTypeSelectionType;
  }
): Promise<Partial<WorkflowsViewFiltersType>> => {
  const baseDefaults = {
    subjectFilterType: WorkflowListSubjectFilterType.ASSET,
    subjectFilterSelection: defaultAssetType,
    assetType: defaultAssetType,
  };

  const merged = await getDefaultValuesAndStoredFilters({
    defaultValues: baseDefaults as WorkflowsViewFiltersType,
    storedFilters: storedFilters as WorkflowsViewFiltersType,
    prioritizeStoredFilters: true,
  });

  return {
    ...merged,
    ...normalizeWorkflowListViewFilters(merged, { defaultAssetType }),
  };
};

type WorkflowListFilterInput = Partial<
  WorkflowsTableFiltersType & WorkflowsViewFiltersType
>;

export type GetWorkflowListSubjectFiltersOptionsType = {
  /** When true, subject filters account for v2 dataset-triggered workflows. */
  isWithinWorkflowTemplateDetails?: boolean;
  defaultAssetType?: (typeof AssetTypeSelection)[keyof typeof AssetTypeSelection];
};

const getWorkflowTemplateDetailsApiVersionFilter = () => ({
  "workflow_template.api_version": [
    WorkflowTemplateApiVersion.V1,
    WorkflowTemplateApiVersion.V2,
  ],
});

const getAssetSubjectFilters = ({
  assetType,
  assetName,
  facilityId,
  equipmentId,
  deviceId,
  flowId,
  assetGroupId,
  workflow,
}: WorkflowListFilterInput) => {
  const isValidId = (assetIds: string[] | undefined) => !!assetIds?.length;

  switch (assetType) {
    case AssetTypeSelection.FACILITY:
      return {
        ...(assetName ? { "facility.name": assetName } : {}),
        ...(isValidId(facilityId)
          ? { "facility.id": facilityId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.facility
          ? toFlattenedObject(workflow.facility, {
              prefix: "facility",
            })
          : {}),
      };
    case AssetTypeSelection.EQUIPMENT:
      return {
        ...(assetName ? { "equipment.name": assetName } : {}),
        ...(isValidId(equipmentId)
          ? { "equipment.id": equipmentId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.equipment
          ? toFlattenedObject(workflow.equipment, {
              prefix: "equipment",
            })
          : {}),
      };
    case AssetTypeSelection.DEVICE:
      return {
        ...(assetName ? { "device.name": assetName } : {}),
        ...(isValidId(deviceId)
          ? { "device.id": deviceId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.device
          ? toFlattenedObject(workflow.device, {
              prefix: "device",
            })
          : {}),
      };
    case AssetTypeSelection.FLOW:
      return {
        ...(assetName ? { "flow.name": assetName } : {}),
        ...(isValidId(flowId)
          ? { "flow.id": flowId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.flow
          ? toFlattenedObject(workflow.flow, {
              prefix: "flow",
            })
          : {}),
      };
    case AssetTypeSelection.ASSET_GROUP:
      return {
        ...(assetName ? { "asset_group.name": assetName } : {}),
        ...(isValidId(assetGroupId)
          ? { "asset_group.id": assetGroupId }
          : { "workflow_template.asset_type": assetType }),
        ...(workflow?.asset_group
          ? toFlattenedObject(workflow.asset_group, {
              prefix: "asset_group",
            })
          : {}),
      };
    default:
      return {};
  }
};

const getDatasetSubjectFilters = (
  { datasetId }: WorkflowListFilterInput,
  {
    isWithinWorkflowTemplateDetails = false,
  }: Pick<
    GetWorkflowListSubjectFiltersOptionsType,
    "isWithinWorkflowTemplateDetails"
  > = {}
) => {
  const apiVersionFilter = {
    "workflow_template.api_version": [
      WorkflowTemplateApiVersion.V1,
      WorkflowTemplateApiVersion.V2,
    ],
  };

  if (datasetId?.length) {
    return {
      ...apiVersionFilter,
      dataset_id: datasetId.length === 1 ? datasetId[0] : datasetId,
    };
  }

  if (isWithinWorkflowTemplateDetails) {
    return apiVersionFilter;
  }

  return {
    ...apiVersionFilter,
    $and: [{ dataset_id: { $exists: true } }],
  };
};

/** Builds workflow list search filters from view and table filter state. */
export const getWorkflowListSubjectFilters = (
  filters: WorkflowListFilterInput,
  options: GetWorkflowListSubjectFiltersOptionsType = {}
) => {
  const { isWithinWorkflowTemplateDetails = false, defaultAssetType } = options;
  const resolvedFilters = defaultAssetType
    ? {
        ...filters,
        ...normalizeWorkflowListViewFilters(filters, { defaultAssetType }),
      }
    : filters;

  if (isWithinWorkflowTemplateDetails) {
    if (isWorkflowListDatasetSubjectFilterActive(resolvedFilters)) {
      return getDatasetSubjectFilters(resolvedFilters, {
        isWithinWorkflowTemplateDetails,
      });
    }

    const assetType =
      resolvedFilters.assetType ??
      defaultAssetType ??
      AssetTypeSelection.ALL_ASSETS;

    if (assetType === AssetTypeSelection.ALL_ASSETS) {
      return getWorkflowTemplateDetailsApiVersionFilter();
    }

    return {
      ...getWorkflowTemplateDetailsApiVersionFilter(),
      ...getAssetSubjectFilters({ ...resolvedFilters, assetType }),
    };
  }

  if (isWorkflowListDatasetSubjectFilterActive(resolvedFilters)) {
    return getDatasetSubjectFilters(resolvedFilters);
  }

  return getAssetSubjectFilters(resolvedFilters);
};
